import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { TextField } from '~common/components/text-field'
import { getCountryOptions, getCountryName } from '~/utils/locale'
import { Icon } from '~common/components/icon/icon'
import { Button } from '~common/components/button'
import { Switch } from '~common/components/switch'
import { ExpandableSection, NonExpandableSection } from '../section-wrapper'
import {
    FormGroup,
    Select,
    ErrorMessage,
    SubSection,
    SubSectionTitle,
    SubSectionDescription,
    SubSectionLabel,
    ToggleContainer,
    ToggleLabel,
    ToggleInput,
    CategoryListContainer,
    CategoryListItem,
    CategoryInfo,
    CategoryName,
    CategoryDesc,
} from '../shared-styles'
import type { SettingsModalLogic } from '../../logic'
import type { PluginId } from '../../../plugins/config'
import styled, { css } from 'styled-components'

interface PluginSettingsSectionProps {
    logic: SettingsModalLogic
    state: {
        countryCode: string
        pluginSettingsExpanded: boolean
        expandedPlugins: Record<PluginId, boolean>
    }
    expanded: boolean
    onToggle: () => void
    showTitleInSection?: boolean
    isSpecificView?: boolean
}

export const PluginSettingsSection: React.FC<PluginSettingsSectionProps> = ({
    logic,
    state,
    expanded,
    onToggle,
    showTitleInSection = true,
    isSpecificView = false,
}) => {
    const { t } = useTranslation('settings')
    const pluginsToDisplay = logic.getPluginsToDisplay()

    if (isSpecificView && pluginsToDisplay.length > 0) {
        return (
            <NonExpandableSection>
                {pluginsToDisplay.map((pluginId) => (
                    <PluginContent
                        key={pluginId}
                        pluginId={pluginId}
                        logic={logic}
                        state={state}
                    />
                ))}
            </NonExpandableSection>
        )
    }

    return (
        <ExpandableSection
            title={t('sections.pluginSettings.title')}
            description={t('sections.pluginSettings.description')}
            expanded={expanded}
            onToggle={onToggle}
            iconName="gear"
            iconColor={({ theme }) => theme.colors.text.primary}
            showTitleInSection={showTitleInSection}
        >
            {pluginsToDisplay.map((pluginId) => (
                <PluginSection key={pluginId}>
                    <PluginSectionContainer
                        onClick={() => logic.togglePluginExpanded(pluginId)}
                        expanded={state.expandedPlugins[pluginId]}
                    >
                        <SectionHeader>
                            <SectionLabel>
                                <SectionTitle>
                                    {logic.getPluginTitle(pluginId)}
                                </SectionTitle>
                                <FoldedDescription>
                                    {logic.getPluginDescription(pluginId)}
                                </FoldedDescription>
                            </SectionLabel>
                            <Icon
                                name="triangle-down"
                                size="sm"
                                iconColor={({ theme }) =>
                                    theme.colors.text.secondary
                                }
                                style={{
                                    transform: state.expandedPlugins[pluginId]
                                        ? 'rotate(0deg)'
                                        : 'rotate(-90deg)',
                                    transition: 'transform 0.2s ease',
                                }}
                            />
                        </SectionHeader>
                        {state.expandedPlugins[pluginId] && (
                            <SectionContent
                                onClick={(e) => e.stopPropagation()}
                            >
                                <PluginContent
                                    pluginId={pluginId}
                                    logic={logic}
                                    state={state}
                                />
                            </SectionContent>
                        )}
                    </PluginSectionContainer>
                </PluginSection>
            ))}
        </ExpandableSection>
    )
}

interface PluginContentProps {
    pluginId: PluginId
    logic: SettingsModalLogic
    state: {
        countryCode: string
        expandedPlugins: Record<PluginId, boolean>
    }
}

const PluginContent: React.FC<PluginContentProps> = ({
    pluginId,
    logic,
    state,
}) => {
    const { t } = useTranslation('settings')

    if (pluginId === 'get-my-invoices') {
        return (
            <SubSection>
                <SubSectionTitle>API Configuration</SubSectionTitle>
                <SubSectionDescription>
                    Please enter your GetMyInvoices API Key. This key will be
                    stored securely.
                </SubSectionDescription>
                <div style={{ marginBottom: '16px' }}>
                    <Button
                        label="Get API Key"
                        onClick={() =>
                            window.open(
                                'https://app.getmyinvoices.com/api_access',
                                '_blank',
                            )
                        }
                        variant="secondary"
                        size="medium"
                    />
                </div>
                <FormGroup>
                    <TextField
                        label="API Key"
                        initValue={
                            logic.getGetMyInvoicesSettings()?.apiKey || ''
                        }
                        onChange={(value: string) =>
                            logic.updateGetMyInvoicesApiKey(value)
                        }
                        editable={true}
                        placeholder="Enter API Key"
                        width="100%"
                        size="large"
                    />
                </FormGroup>
            </SubSection>
        )
    }

    if (pluginId !== 'datev-classify-transactions') {
        return null
    }

    const displayValue = state.countryCode
        ? getCountryName(state.countryCode)
        : ''

    // Get current datevSettings once per render (cheap)
    const datevSettings = logic.getDatevSettings()
    const activeCategories = datevSettings?.activeCategories || []

    return (
        <>
            <SubSection>
                <SubSectionTitle>
                    {t('sections.pluginSettings.generalSettings')}
                </SubSectionTitle>
                <FormGroup>
                    <TextField
                        label={t('sections.pluginSettings.countryOfCompany')}
                        initValue={displayValue}
                        onChange={(countryCode: string) =>
                            logic.updateCountryCode(countryCode)
                        }
                        editable={true}
                        dropdown={true}
                        options={getCountryOptions()}
                        placeholder={t('sections.companyInfo.selectCountry')}
                        width="100%"
                        size="large"
                    />
                </FormGroup>
                {state.countryCode === 'de' && (
                    <>
                        <FormGroup>
                            <SubSectionLabel>
                                {t('sections.pluginSettings.kontenrahmen')}
                            </SubSectionLabel>
                            <Select
                                value={
                                    logic.getDatevSettings()?.kontenrahmen || ''
                                }
                                onChange={(e) =>
                                    logic.updateKontenrahmen(
                                        e.target.value as
                                            | 'SKR03'
                                            | 'SKR04'
                                            | undefined,
                                    )
                                }
                            >
                                <option value="">
                                    {t(
                                        'sections.pluginSettings.selectKontenrahmen',
                                    )}
                                </option>
                                <option value="SKR03">SKR03</option>
                                <option value="SKR04">SKR04</option>
                            </Select>
                        </FormGroup>
                        <FormGroup>
                            <ToggleContainer>
                                <ToggleLabel>
                                    {t(
                                        'sections.pluginSettings.kleinunternehmer',
                                    )}
                                </ToggleLabel>
                                <ToggleInput
                                    type="checkbox"
                                    checked={
                                        logic.getDatevSettings()
                                            ?.kleinunternehmer || false
                                    }
                                    onChange={(e) =>
                                        logic.updateKleinunternehmer(
                                            e.target.checked,
                                        )
                                    }
                                />
                            </ToggleContainer>
                        </FormGroup>
                    </>
                )}
            </SubSection>

            {state.countryCode && state.countryCode !== 'de' && (
                <ErrorMessage>
                    {t('sections.pluginSettings.unsupportedCountry')}
                </ErrorMessage>
            )}

            {state.countryCode === 'de' && (
                <>
                    <SubSection>
                        <SubSectionTitle>
                            {t('sections.pluginSettings.customInstructions')}
                        </SubSectionTitle>
                        <FormGroup>
                            <TextField
                                label={t(
                                    'sections.pluginSettings.customInstructionsLabel',
                                )}
                                initValue={
                                    logic.getDatevSettings()
                                        ?.customInstructions || ''
                                }
                                onChange={logic.updateCustomInstructions}
                                editable={true}
                                multiline={true}
                                placeholder={t(
                                    'sections.pluginSettings.customInstructionsPlaceholder',
                                )}
                                width="100%"
                                size="large"
                            />
                        </FormGroup>
                    </SubSection>

                    {logic.getDatevSettings()?.kontenrahmen &&
                        logic.getDatevSettings()?.kontenrahmen !==
                            undefined && (
                            <SubSection>
                                <SubSectionTitle>
                                    {t(
                                        'sections.pluginSettings.availableCategories',
                                    )}
                                </SubSectionTitle>
                                <SubSectionDescription>
                                    {t(
                                        'sections.pluginSettings.availableCategoriesDescription',
                                    )}
                                </SubSectionDescription>
                                <FormGroup>
                                    <TextField
                                        initValue={
                                            logic.getDatevSettings()
                                                ?.categorySearchQuery || ''
                                        }
                                        onChange={
                                            logic.updateCategorySearchQuery
                                        }
                                        editable={true}
                                        placeholder={t(
                                            'sections.pluginSettings.searchCategoriesPlaceholder',
                                        )}
                                        width="100%"
                                        size="medium"
                                        onClear={() =>
                                            logic.updateCategorySearchQuery('')
                                        }
                                    />
                                </FormGroup>
                                <CategoryListContainer>
                                    {/* Simple map on pre-sorted filtered list (no IIFE, no sort) */}
                                    {logic
                                        .getAvailableCategories()
                                        .map((category) => {
                                            const isActive =
                                                activeCategories.includes(
                                                    Number(category.id),
                                                )
                                            return (
                                                <CategoryListItem
                                                    key={category.id}
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        logic.toggleCategoryActive(
                                                            Number(category.id),
                                                        )
                                                    }}
                                                >
                                                    <CategoryInfo>
                                                        <CategoryName>
                                                            {category.id} -{' '}
                                                            {category.label}
                                                        </CategoryName>
                                                        {category.description && (
                                                            <CategoryDesc>
                                                                {
                                                                    category.description
                                                                }
                                                            </CategoryDesc>
                                                        )}
                                                    </CategoryInfo>
                                                    <Switch
                                                        checked={isActive}
                                                    />
                                                </CategoryListItem>
                                            )
                                        })}
                                </CategoryListContainer>
                            </SubSection>
                        )}
                </>
            )}
        </>
    )
}

const PluginSection = styled.div`
    margin-bottom: ${({ theme }) => theme.spacing[3]};
`

const PluginSectionContainer = styled.div<{ expanded: boolean }>`
    border-radius: ${({ theme }) => theme.borderRadius.md};
    padding: ${({ theme }) => theme.spacing[2]};
    box-sizing: border-box;
    border: 1px solid ${({ theme }) => theme.colors.greyScale[300]};
    background-color: ${({ theme }) => theme.colors.background.bg1};

    ${({ expanded }) =>
        !expanded &&
        css`
            &:hover {
                background-color: ${({ theme }) => theme.colors.background.bg2};
            }
        `}
`

const SectionHeader = styled.div`
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    gap: ${({ theme }) => theme.spacing[8]};
    padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
    border-radius: ${({ theme }) => theme.borderRadius.sm};
    box-sizing: border-box;
    transition: background-color ${({ theme }) => theme.transitions.fast};
`

const SectionLabel = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: ${({ theme }) => theme.spacing[1]};
`

const SectionTitle = styled.span`
    font-size: ${({ theme }) => theme.typography.fontSize.xl};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    color: ${({ theme }) => theme.colors.text.primary};
`

const FoldedDescription = styled.span`
    color: ${({ theme }) => theme.colors.text.secondary};
    margin-top: ${({ theme }) => theme.spacing[2]};
    font-size: ${({ theme }) => theme.typography.fontSize.base};
`

const SectionContent = styled.div`
    padding: ${({ theme }) => theme.spacing[4]};
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[4]};
    border-top: 1px solid ${({ theme }) => theme.colors.greyScale[300]};
    width: 100%;
`
